import React, { useRef, useState, useEffect } from "react"
import {
    Modal,
    Tooltip,
    Typography,
    IconButton,
    Box
} from "@mui/material"
import { makeStyles } from "@mui/styles"
import {
    Close,
    Fullscreen,
    FullscreenExit
} from "@mui/icons-material"
import _ from "lodash"

const useStyles = makeStyles(() => ({
    backdrop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16
    },
    root: {
        backgroundColor: "#fff",
        borderRadius: 8,
        width: "100%",
        maxWidth: 420,
        height: "80vh",
        maxHeight: 640,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
    },
    fullscreen: {
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        borderRadius: 0
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        borderBottom: "1px solid #eee",
        backgroundColor: "#f9f9f9"
    },
    body: {
        flex: 1,
        overflow: "hidden"
    },
    iframe: {
        width: "100%",
        height: "100%",
        border: "none"
    }
}))

export default function DocViewer({
    isOpen = false,
    onClose,
    data
}) {
    const classes = useStyles()
    const viewerRef = useRef(null)
    const [isFullscreen, setIsFullscreen] = useState(false)

    /* 🔁 Sync fullscreen state */
    useEffect(() => {
        const handler = () => {
            setIsFullscreen(Boolean(document.fullscreenElement))
        }
        document.addEventListener("fullscreenchange", handler)
        return () =>
            document.removeEventListener("fullscreenchange", handler)
    }, [])

    if (!data) return null

    const dobCerfiticate = _.get(data, "meta_data.dobCerfiticate", null)
    const url = dobCerfiticate && JSON.parse(dobCerfiticate)?.url

    /* 🧠 Enter fullscreen */
    const enterFullscreen = () => {
        if (viewerRef.current?.requestFullscreen) {
            viewerRef.current.requestFullscreen()
        }
    }

    /* 🧠 Exit fullscreen */
    const exitFullscreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen()
        }
    }

    /* ❌ Close handler */
    const handleClose = () => {
        exitFullscreen()
        onClose?.()
    }

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            className={classes.backdrop}
        >
            <Box
                ref={viewerRef}
                className={`${classes.root} ${isFullscreen ? classes.fullscreen : ""
                    }`}
            >
                {/* Header */}
                <Box className={classes.header}>
                    <Typography fontWeight="bold">
                        View Document
                    </Typography>

                    <Box>
                        <Tooltip
                            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        >
                            <IconButton
                                size="small"
                                onClick={
                                    isFullscreen ? exitFullscreen : enterFullscreen
                                }
                            >
                                {isFullscreen ? (
                                    <FullscreenExit />
                                ) : (
                                    <Fullscreen />
                                )}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Close">
                            <IconButton size="small" onClick={handleClose}>
                                <Close />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Body */}
                <Box className={classes.body}>
                    {url ? (
                        <iframe
                            src={url}
                            title="Document Viewer"
                            className={classes.iframe}
                        />
                    ) : (
                        <Typography align="center" sx={{ mt: 4 }}>
                            No document available
                        </Typography>
                    )}
                </Box>
            </Box>
        </Modal>
    )
}
